import React, { useEffect, useState } from 'react';
import './AccountTypeForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';

function AccountTypeForm({ initial, accountClasses, onSave, onCancel, loading }) {
  const [account_class_id, setAccountClassId] = useState(initial?.account_class_id || '');
  const [account_number, setAccountNumber] = useState(initial?.account_number ?? '');
  const [type_code, setTypeCode] = useState(initial?.type_code || '');
  const [type_name, setTypeName] = useState(initial?.type_name || '');
  const [normal_balance, setNormalBalance] = useState(initial?.normal_balance || 'debit');
  const [allow_posting, setAllowPosting] = useState(initial?.allow_posting ?? true);
  const [allow_manual_journal, setAllowManualJournal] = useState(initial?.allow_manual_journal ?? true);
  const [vendor_control, setVendorControl] = useState(initial?.vendor_control ?? false);
  const [customer_control, setCustomerControl] = useState(initial?.customer_control ?? false);
  const [inventory_control, setInventoryControl] = useState(initial?.inventory_control ?? false);
  const [tax_control, setTaxControl] = useState(initial?.tax_control ?? false);
  const [is_bank, setIsBank] = useState(initial?.is_bank ?? false);
  const [sort_order, setSortOrder] = useState(initial?.sort_order || 100);
  const [system_locked, setSystemLocked] = useState(initial?.system_locked ?? false);
  const [description, setDescription] = useState(initial?.description || '');
  const [error, setError] = useState('');

  useEffect(() => {
    setAccountClassId(initial?.account_class_id || '');
    setAccountNumber(initial?.account_number ?? '');
    setTypeCode(initial?.type_code || '');
    setTypeName(initial?.type_name || '');
    setNormalBalance(initial?.normal_balance || 'debit');
    setAllowPosting(initial?.allow_posting ?? true);
    setAllowManualJournal(initial?.allow_manual_journal ?? true);
    setVendorControl(initial?.vendor_control ?? false);
    setCustomerControl(initial?.customer_control ?? false);
    setInventoryControl(initial?.inventory_control ?? false);
    setTaxControl(initial?.tax_control ?? false);
    setIsBank(initial?.is_bank ?? false);
    setSortOrder(initial?.sort_order || 100);
    setSystemLocked(initial?.system_locked ?? false);
    setDescription(initial?.description || '');
  }, [initial]);

  const validate = () => {
    if (!account_class_id) return 'Account class is required.';
    if (!String(account_number).match(/^\d+$/) || Number(account_number) < 1) return 'Account number must be a positive whole number.';
    if (!type_code.trim()) return 'Type code is required.';
    if (!/^[a-z0-9_]{2,20}$/.test(type_code.trim())) return 'Type code must be 2-20 lowercase letters, numbers or underscores.';
    if (!type_name.trim()) return 'Type name is required.';
    if (!String(sort_order).match(/^\d+$/) || Number(sort_order) < 1) return 'Sort order must be a positive number.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    setError('');
    onSave({
      account_class_id: Number(account_class_id),
      account_number: Number(account_number),
      type_code: type_code.trim().toLowerCase(),
      type_name: type_name.trim(),
      normal_balance,
      allow_posting,
      allow_manual_journal,
      vendor_control,
      customer_control,
      inventory_control,
      tax_control,
      is_bank,
      sort_order: Number(sort_order),
      system_locked,
      description: description.trim(),
    });
  };

  return (
    <form className="account-type-form" onSubmit={handleSubmit} style={{ minWidth: 360 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 220 }}>
          <label>Account Class<RequiredMark /></label><br />
          <select className="styled-input" value={account_class_id} onChange={(e) => setAccountClassId(e.target.value)}>
            <option value="">Select Account Class</option>
            {accountClasses.map((cls) => (
              <option key={cls.id} value={cls.id}>{cls.class_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Account Number<RequiredMark /></label><br />
          <input
            className="styled-input"
            type="number"
            min="1"
            value={account_number}
            onChange={(e) => setAccountNumber(e.target.value)}
            style={{ width: 100 }}
            placeholder="e.g., 1010"
          />
        </div>
        <div>
          <label>Type Code<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={type_code}
            onChange={(e) => setTypeCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20))}
            maxLength={20}
            placeholder="e.g., cash_on_hand"
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260 }}>
          <label>Type Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={type_name}
            onChange={(e) => setTypeName(e.target.value)}
            maxLength={100}
            placeholder="e.g., Cash on Hand"
          />
        </div>
        <div>
          <label>Normal Balance<RequiredMark /></label><br />
          <select className="styled-input" value={normal_balance} onChange={(e) => setNormalBalance(e.target.value)}>
            <option value="debit">Debit</option>
            <option value="credit">Credit</option>
          </select>
        </div>
        <div>
          <label>Sort Order<RequiredMark /></label><br />
          <input
            className="styled-input"
            type="number"
            min="1"
            value={sort_order}
            onChange={(e) => setSortOrder(e.target.value)}
            style={{ width: 90 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={allow_posting} onChange={(e) => setAllowPosting(e.target.checked)} />
          Allow Posting
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={allow_manual_journal} onChange={(e) => setAllowManualJournal(e.target.checked)} />
          Allow Manual Journal
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={vendor_control} onChange={(e) => setVendorControl(e.target.checked)} />
          Vendor Control
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={customer_control} onChange={(e) => setCustomerControl(e.target.checked)} />
          Customer Control
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={inventory_control} onChange={(e) => setInventoryControl(e.target.checked)} />
          Inventory Control
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={tax_control} onChange={(e) => setTaxControl(e.target.checked)} />
          Tax Control
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Accounts under this type hold bank details and can be picked as where a payment was paid into">
          <input type="checkbox" checked={is_bank} onChange={(e) => setIsBank(e.target.checked)} />
          Bank Type
        </label>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 10 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={system_locked} onChange={(e) => setSystemLocked(e.target.checked)} />
          System Locked
        </label>
      </div>

      <div style={{ marginTop: 10 }}>
        <label>Description</label><br />
        <textarea
          className="styled-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={{ width: '100%', resize: 'vertical' }}
          placeholder="Optional notes"
        />
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default AccountTypeForm;
